import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';
import { CombinedLongeingData, Recording, RecordingActionSuccessTypeEnum } from '@sleipai/sleip-models';
import * as RecordingActions from '../actions/recording.actions';

export const recordingFeatureKey = 'recording';

export interface State extends EntityState<Recording> {
  totalRecordingsCount: number;
  loading: boolean;
  loaded: boolean;
  error: string | any | null;
  offset: any | null;
  reviewStatus: string | null;
  loadingList: boolean;
  loadingListCount: boolean;
  recordingRemoved: boolean;
  updatingRecording: boolean;
  updatedRecording: boolean;
  hasMore: boolean;
  recordingActionSuccessType: RecordingActionSuccessTypeEnum | null;
  batchMoveFailedIds: string[] | null;
  batchRemoveFailedIds: string[] | null;
  combinedLongeings: CombinedLongeingData[];
}

export function selectRecordingId(a: Recording) {
  return a.id ?? '';
}

export function sortByName(a: Recording, b: Recording) {
  return a.id?.localeCompare(b.id ?? '') ?? -1;
}

export const adapter: EntityAdapter<any> = createEntityAdapter<any>({
  selectId: selectRecordingId,
  sortComparer: sortByName,
});

export const initialState: State = adapter.getInitialState({
  totalRecordingsCount: 0,
  loading: false,
  loaded: false,
  error: null,
  offset: null,
  reviewStatus: null,
  loadingList: true,
  loadingListCount: true,
  recordingRemoved: false,
  updatingRecording: false,
  updatedRecording: false,
  hasMore: true,
  recordingActionSuccessType: null,
  batchMoveFailedIds: null,
  batchRemoveFailedIds: null,
  combinedLongeings: [],
});

export const recordingReducer = createReducer(
  initialState,
  on(RecordingActions.loadRecordings, (state) => {
    return {
      ...state,
      loading: true,
    };
  }),
  on(RecordingActions.loadSingleRecording, (state) => {
    return {
      ...state,
      loading: true,
    };
  }),
  on(RecordingActions.addSingleRecording, (state, { recording }) => {
    return adapter.upsertOne(recording, {
      ...state,
      loaded: true,
      loading: false,
    });
  }),
  on(RecordingActions.addRecordings, (state, { recordings, hasMore }) => {
    return adapter.upsertMany(recordings, {
      ...state,
      loading: false,
      loaded: true,
      hasMore,
    });
  }),
  on(RecordingActions.loadRecordingListByType, (state, { status, offset }) => {
    return {
      ...state,
      loaded: false,
      loading: true,
      reviewStatus: status ?? null,
      offset: offset ?? null,
    };
  }),
  on(
    RecordingActions.loadRecordingListByTypeSuccess,
    (state, { recordings }) => {
      return adapter.upsertMany(recordings, {
        ...state,
        loading: false,
        loaded: true,
      });
    }
  ),
  on(RecordingActions.loadRecordingsCountInFirestoreDb, (state) => {
    return {
      ...state,
      loadingListCount: true,
      loaded: false,
    };
  }),
  on(RecordingActions.loadRecordingsListInFirestoreDb, (state) => {
    return {
      ...state,
      loadingList: true,
      loaded: false,
    };
  }),
  on(
    RecordingActions.loadRecordingsCountInFirestoreDbSuccess,
    (state, { count }) => {
      return {
        ...state,
        totalRecordingsCount: count,
        loadingListCount: false,
        loaded: true,
      };
    }
  ),
  on(
    RecordingActions.loadRecordingsListInFirestoreDbSuccess,
    (state, { data }) => {
      if (data && data.length) {
        return adapter.upsertMany(data, {
          ...state,
          loadingList: false,
          loaded: true,
          loading: false,
        });
      } else {
        return {
          ...state,
          entities: {},
          ids: [],
          loadingList: false,
          loaded: true,
          loading: false,
        };
      }
    }
  ),
  on(RecordingActions.loadRecordingsFailure, (state, { error }) => {
    return {
      ...state,
      loading: false,
      loadingList: false,
      loadingListCount: false,
      loaded: true,
      error: error,
    };
  }),
  on(RecordingActions.removeRecording, (state) => {
    return {
      ...state,
      loading: true,
    };
  }),
  on(RecordingActions.removeRecordingSuccess, (state, { recordingId }) => {
    return adapter.removeOne(recordingId, {
      ...state,
      loading: false,
      recordingRemoved: true,
    });
  }),
  on(RecordingActions.removeRecordingFailure, (state, { error }) => {
    return {
      ...state,
      loading: false,
      error: error?.message ?? error,
    };
  }),
  on(RecordingActions.resetRemoveRecordingFlag, (state) => {
    return {
      ...state,
      recordingRemoved: false,
    };
  }),
  // Infinite recording list pagination actions start
  on(RecordingActions.loadRecordingsInfiniteScrollListPageForHorse, (state) => {
    return {
      ...state,
      loadingList: true,
      loaded: false,
    };
  }),
  on(
    RecordingActions.loadRecordingsInfiniteScrollListPageForHorseSuccess,
    (state, { recordingList }) => {
      return adapter.upsertMany(recordingList, {
        ...state,
        loadingList: false,
        loaded: true,
      });
    }
  ),

  //// Dashboard 2.0 recordings list pagination start ////
  on(
    RecordingActions.loadRecordingsListForInfiniteScrollInDashBoard,
    (state, { reset }) => {
      return reset
        ? adapter.removeAll({
          ...state,
          loaded: false,
          loadingList: true,
          error: null,
          hasMore: true,
        })
        : {
          ...state,
          loaded: false,
          loadingList: true,
          error: null,
        };
    }
  ),
  on(
    RecordingActions.loadRecordingsListForInfiniteScrollInDashBoardSuccess,
    (state, { recordings, hasMore }) => {
      return adapter.upsertMany(recordings, {
        ...state,
        loaded: true,
        loadingList: false,
        error: null,
        hasMore,
      });
    }
  ),
  on(
    RecordingActions.loadRecordingsCountForInfiniteScrollInDashBoard,
    (state, { reset }) => {
      return reset
        ? {
          ...state,
          loadingListCount: true,
          error: null,
        }
        : {
          ...state,
          error: null,
        };
    }
  ),
  on(
    RecordingActions.loadRecordingsCountForInfiniteScrollInDashBoardSuccess,
    (state, { recordingsCount }) => {
      return {
        ...state,
        totalRecordingsCount: recordingsCount,
        loadingListCount: false,
      };
    }
  ),
  on(
    RecordingActions.resetRecordingsForInfiniteScrollInDashBoard,
    () => initialState
  ),
  on(
    RecordingActions.retryLoadRecordingsForInfiniteScrollInDashBoard,
    (state) => ({
      ...state,
      error: null,
    })
  ),
  //// Dashboard 2.0 recordings list pagination end ////

  // Infinite recording list pagination actions end
  on(RecordingActions.updateRecordingData, (state) => {
    return {
      ...state,
      updatingRecording: true,
    };
  }),
  on(RecordingActions.updateRecordingDataSuccess, (state) => {
    return {
      ...state,
      updatingRecording: false,
      updatedRecording: true,
    };
  }),
  on(RecordingActions.loadEligibleRecordingsToLinkForARecording, (state) => {
    return {
      ...state,
    };
  }),
  on(
    RecordingActions.loadEligibleRecordingsToLinkForCombinedViewForRecordingSuccess,
    (state, { recordings }) => {
      return adapter.upsertMany(recordings, {
        ...state,
        loading: false,
        loaded: true,
        loadingList: false,
        error: null,
      });
    }
  ),
  on(RecordingActions.updateCombinedViewLinkedRecording, (state) => {
    return {
      ...state,
      updatingRecording: true,
      updatedRecording: false,
    };
  }),
  on(
    RecordingActions.recordingLinkingForCombinedViewRelatedOperationFailure,
    (state, { error }) => {
      return {
        ...state,
        error: error?.message ?? error,
      };
    }
  ),
  on(RecordingActions.updateRecordingDataFailure, (state) => {
    return {
      ...state,
      updatingRecording: false,
      updatedRecording: false,
    };
  }),
  on(RecordingActions.resetRecordingUpdatedFlag, (state) => {
    return {
      ...state,
      updatingRecording: false,
      updatedRecording: false,
    };
  }),
  on(RecordingActions.batchRemoveRecordings, (state) => {
    return { ...state, loading: true, error: null, recordingActionSuccessType: null };
  }),
  on(RecordingActions.batchRemoveRecordingsSuccess, (state, { removedIds, failedIds, recordingActionSuccessType }) => {
    return adapter.removeMany(removedIds, {
      ...state,
      loading: false,
      recordingActionSuccessType,
      batchRemoveFailedIds: failedIds,
    });
  }),
  on(RecordingActions.batchRemoveRecordingsFailure, (state, { error }) => {
    return { ...state, loading: false, error: error?.message ?? error };
  }),
  on(RecordingActions.batchMoveRecordingsToHorse, (state) => {
    return {
      ...state,
      loading: true,
      error: null,
      recordingActionSuccessType: null,
    };
  }),
  on(RecordingActions.batchMoveRecordingsToHorseSuccess, (state, { movedIds, failedIds, recordingActionSuccessType }) => {
    return adapter.removeMany(movedIds, {
      ...state,
      loading: false,
      totalRecordingsCount: state.totalRecordingsCount - movedIds.length,
      recordingActionSuccessType,
      batchMoveFailedIds: failedIds,
    });
  }),
  on(RecordingActions.batchMoveRecordingsToHorseFailure, (state, { error }) => {
    return {
      ...state,
      loading: false,
      error: error?.message ?? error,
    };
  }),
  on(
    RecordingActions.loadCombinedLongeingsForHorse,
    RecordingActions.clearCombinedLongeings,
    (state) => {
      return {
        ...state,
        combinedLongeings: [],
      };
    }
  ),
  on(RecordingActions.setCombinedLongeingsForHorse, (state, { combinedLongeings }) => {
    return {
      ...state,
      combinedLongeings: combinedLongeings ?? [],
    };
  }),
  on(RecordingActions.loadCombinedLongeingsForHorseFailure, (state, { error }) => {
    return {
      ...state,
      error: error?.message ?? error,
      combinedLongeings: [],
    };
  })
);

// get the selectors
export const {
  selectIds: selectRecordingIdsAdapter,
  selectEntities: selectRecordingEntitiesAdapter,
  selectAll: selectAllRecordingAdapter,
  selectTotal: selectRecordingTotalAdapter,
} = adapter.getSelectors();

export function reducer(state: State | undefined, action: Action) {
  return recordingReducer(state, action);
}
